import { useKycFinishKycVerificationMutation } from '@e-kyc/graphql';
import { useTranslation } from '@e-kyc/translations';
import { useState } from 'react';

import { Button, LoadingSpinner, Modal } from '../../../../../components';
import { useEkyc, type EkycStep } from '../../../../../providers';
import { isAlreadyCompletedError } from '../../../utils';

interface CaptureFrontModalProps {
  isOpen: boolean;
  isLoading: boolean;
  isEligibleToSkipVerification: boolean;
  setStep: (step: EkycStep) => void;
  resetCamera: () => void;
  close: () => void;
}

function CaptureFaceModal(props: CaptureFrontModalProps) {
  const { isOpen, isLoading, resetCamera, setStep, close, isEligibleToSkipVerification } = props;

  const [finishKycVerification, { loading }] = useKycFinishKycVerificationMutation();

  // Held from the moment an ALREADY_COMPLETED error arrives until the provider's status gate
  // swaps the page, so the button does not re-enable on a record that is already finished.
  const [isAwaitingCompletedSwap, setIsAwaitingCompletedSwap] = useState(false);

  const { userId, refetchEkycStatus } = useEkyc();

  const { t } = useTranslation();
  return (
    <Modal open={isOpen} className='w-full max-w-[343px]'>
      {isLoading ? (
        <div className='flex w-full flex-col items-center justify-center gap-4 py-6'>
          <div className='flex size-12 flex-col items-center justify-center'>
            <LoadingSpinner />
          </div>
          <h5>{t('capture.face.modal.loading')}</h5>
        </div>
      ) : (
        <div className='flex w-full flex-col items-center justify-center gap-4'>
          <div className='flex flex-col items-center justify-center gap-4 pt-4'>
            <img src='face_verification_failed.png' alt='face verification failed icon' />
            <div className='flex flex-col items-center justify-center gap-2'>
              <h5 className='text-error mt-4 font-semibold'>
                {t('capture.face.modal.failed.title')}
              </h5>
              <div className='text-body-1 text-text-secondary text-center'>
                {isEligibleToSkipVerification
                  ? t('capture.face.modal.failed.description.deny')
                  : t('capture.face.modal.failed.description.allow')}
              </div>
            </div>
          </div>
          {isEligibleToSkipVerification ? (
            <>
              <div className='flex w-full flex-col gap-2 p-4'>
                <Button
                  className='w-full'
                  loading={loading || isAwaitingCompletedSwap}
                  onClick={async () => {
                    await finishKycVerification({
                      variables: {
                        finishKycVerificationInput: {
                          userId,
                        },
                      },
                      onCompleted: () => {
                        close();
                        setStep('completed');
                      },
                      onError: (error) => {
                        if (isAlreadyCompletedError(error)) {
                          // e-KYC was completed in another tab - keep the button busy and
                          // let the provider's status gate swap in the completed page
                          setIsAwaitingCompletedSwap(true);
                          refetchEkycStatus()
                            .catch((refetchError) => {
                              console.error('Failed to refetch e-KYC status:', refetchError);
                            })
                            .finally(() => {
                              setIsAwaitingCompletedSwap(false);
                            });
                          return;
                        }
                      },
                    });
                  }}
                >
                  {t('capture.face.modal.continue.deny')}
                </Button>
                <Button
                  className='w-full'
                  variant='outlined'
                  onClick={() => {
                    close();
                    resetCamera();
                  }}
                >
                  {t('capture.retry')}
                </Button>
              </div>
            </>
          ) : (
            <div className='w-full p-4'>
              <Button
                className='w-full'
                onClick={() => {
                  close();
                  resetCamera();
                }}
              >
                {t('button.retry')}
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default CaptureFaceModal;
